import { RefreshCw, Users } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { loadAudienceRecords, type AudienceRecords } from './viewers-api';
import './audience-records.css';

function viewerName(item: { twitchUserId: string; login: string | null; displayName: string | null }): string {
  return item.displayName ?? item.login ?? item.twitchUserId;
}

function preferenceLabel(value: string): string {
  if (value === 'favourite') return 'Favourite';
  if (value === 'community') return 'Community';
  if (value === 'never') return 'Never raid';
  return value.replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()) || 'Unknown';
}

export function AudienceRecordsSection({ showLoyaltyBalances = true }: { showLoyaltyBalances?: boolean } = {}) {
  const [records, setRecords] = useState<AudienceRecords | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const refresh = useCallback(async () => {
    try {
      setRecords(await loadAudienceRecords());
      setMessage('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Audience records could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <section className="ffs-audience-records-section" aria-label="Audience records">
      {message ? <div className="ffs-workspace-message" aria-live="polite">{message}</div> : null}
      <article className="ffs-workspace-card ffs-audience-records-card">
        <header>
          <div><small>AUDIENCE RECORDS</small><h3>{showLoyaltyBalances ? 'Loyalty & raid preferences' : 'Raid preferences'}</h3><p>{showLoyaltyBalances ? 'Loyalty balances and saved raid preferences.' : 'Saved raid preferences used by Raid Finder. Loyalty balances are managed above.'}</p></div>
          <button type="button" className="ffs-button secondary" disabled={loading} onClick={() => void refresh()}><RefreshCw size={14} /> Refresh</button>
        </header>

        {loading && !records ? <div className="ffs-empty-state">Loading audience records…</div> : records ? (
          <div className={`ffs-audience-records-grid ${showLoyaltyBalances ? '' : 'raid-only'}`}>
            {showLoyaltyBalances ? <section>
              <div className="ffs-audience-subhead"><div><strong>Loyalty balances</strong><span>Highest balance first</span></div><b>{records.loyalty.length.toLocaleString('en-GB')}</b></div>
              <div className="ffs-audience-list">
                {records.loyalty.length === 0 ? <div className="ffs-empty-state">No loyalty records yet.</div> : records.loyalty.map((item) => (
                  <div className="ffs-audience-row" key={item.twitchUserId}>
                    <div><strong>{viewerName(item)}</strong><small>{item.login ? `@${item.login}` : `Twitch ${item.twitchUserId}`}</small></div>
                    <span>{Number.isFinite(item.balance) ? item.balance.toLocaleString('en-GB') : '0'}</span>
                  </div>
                ))}
              </div>
            </section> : null}

            <section>
              <div className="ffs-audience-subhead"><div><strong>Raid preferences</strong><span>Saved raid-finder guidance</span></div><b>{records.raidPreferences.length.toLocaleString('en-GB')}</b></div>
              <div className="ffs-audience-list">
                {records.raidPreferences.length === 0 ? <div className="ffs-empty-state">No raid preferences yet.</div> : records.raidPreferences.map((item) => (
                  <div className="ffs-audience-row preference" key={item.twitchUserId}>
                    <div><strong>{viewerName(item)}</strong><small>{item.reason ?? (item.login ? `@${item.login}` : `Twitch ${item.twitchUserId}`)}</small></div>
                    <span>{preferenceLabel(item.preference)}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        ) : (
          <div className="ffs-empty-state"><Users size={16} /> Audience data is unavailable. Use Refresh to retry.</div>
        )}
      </article>
    </section>
  );
}
